import React from 'react';
import { useReportRedirect } from '@/reports/useReportRedirect/useReportRedirect';
import { Card, Stack, Text, Button, Icons, styled } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';

export interface PersonSearchCtaProps {
  /** The image of the banner */
  image?: string;
  /** The name of the person */
  personName: string;
  /** The location of the person */
  location?: string;
}

function parseCityState(input: string): { city: string | null; state: string } {
  if (!input || input === 'Unknown' || input === 'N/A') {
    return { city: null, state: 'All' };
  }

  const regex = /^(?:(?![A-Z]{2}$)([a-zA-Z\s]+),?\s*)?([A-Z]{2})?$/;
  const match = input.match(regex);

  return {
    city: match?.[1]?.trim() || null,
    state: match?.[2] || 'All',
  };
}

/**
 * This component is responsible for displaying the banner
 * promoting the person search in the side nav.
 */
export function PersonSearchCta({
  image,
  personName,
  location = '',
}: PersonSearchCtaProps) {
  const { redirectToSearchPersonUrl } = useReportRedirect();
  const { isMobile, isMedium } = useScreenSize();
  const { city, state } = parseCityState(location);

  function onBeginSearchClick() {
    const personInfo = {
      fName: personName.split(' ')[0],
      mName: null,
      lName: personName.split(' ').slice(-1)[0],
      city,
      state,
      age: null,
    };
    redirectToSearchPersonUrl(personInfo);
  }

  return (
    <Card
      sx={(theme) => ({
        margin: 'auto',
        width: isMobile ? '280px' : '100%',
        height: 'fit-content',
        position: 'relative',
        boxShadow: 'none',
        border: `1px solid ${theme.palette.secondary.main}`,
        top: theme.spacing(1),
      })}
    >
      <Stack
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'center' : 'flex-start'}
        justifyContent="center"
        width="100%"
      >
        <StyledImg src={image} alt="Person search illustration" />
        <Stack
          flexDirection="column"
          justifyContent="space-between"
          height={isMobile ? 'auto' : '200px'}
          padding={isMobile ? 0 : 3}
          marginLeft={isMobile ? 0 : 2}
          maxWidth={isMobile ? '100%' : '50%'}
        >
          <Text
            variant="h2"
            textAlign={isMobile ? 'center' : 'left'}
            marginBottom={0}
          >
            Learn More about {personName.split(' ')[0]}
          </Text>
          <Text
            variant="body1"
            style={{
              color: '#000',
              opacity: '0.6',
            }}
            marginTop={isMobile ? '15px' : '0'}
          >
            Explore their <strong>personal report</strong>, and{' '}
            <strong>discover their family, contact information, assets</strong>{' '}
            and <strong>more</strong>
          </Text>
          <Button
            variant="contained"
            color="success"
            sx={{
              width: isMobile ? '100%' : 'fit-content',
              marginTop: isMedium ? 2 : 0,
            }}
            onClick={onBeginSearchClick}
          >
            <Text mr={1}>Search now </Text>
            <Icons.ArrowForward />
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

const StyledImg = styled('img')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));
